import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { leadService, userService, whatsappService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  PencilIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhoneArrowUpRightIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { formatIST } from '../utils/dates';   // dates on an India clock
import { Spinner } from '../components/Spinner';
import { CrmSyncBadge, useCrmSyncStatus } from '../components/CrmSyncBadge';

const STATUS_DOT = { queued: 'bg-gray-300', sent: 'bg-blue-400', delivered: 'bg-blue-500', read: 'bg-emerald-500', failed: 'bg-red-500' };

const BUYING_TIME_LABELS = {
  immediate:       'Immediate',
  within_1_month:  'Within 1 Month',
  within_3_months: 'Within 3 Months',
  within_6_months: 'Within 6 Months',
  just_enquiring:  'Just Enquiring',
  other:           'Other',
};

// Shows the customer's original custom answer (e.g. an unrecognized
// Facebook lead-form response) instead of the generic "Other".
function buyingTimeLabel(leadInfo) {
  if (leadInfo?.buyingTime === 'other' && leadInfo?.buyingTimeRaw) return leadInfo.buyingTimeRaw;
  return BUYING_TIME_LABELS[leadInfo?.buyingTime] || leadInfo?.buyingTime || '—';
}

// Older Facebook-imported leads may have underscores baked into free-text
// values (e.g. "Maruti_Suzuki") from before this was cleaned on ingestion.
function cleanDisplay(text) {
  return (text || '').replace(/_/g, ' ').trim();
}

// Counts {{1}}, {{2}}... placeholders in a template's BODY component text.
const countBodyParams = (template) => {
  const body = template?.components?.find(c => c.type === 'BODY');
  if (!body?.text) return 0;
  const matches = body.text.match(/\{\{\d+\}\}/g);
  return matches ? matches.length : 0;
};

const WhatsAppThread = ({ leadId }) => {
  const queryClient = useQueryClient();
  const [templateName, setTemplateName] = useState('');
  const [params, setParams] = useState([]);

  const { data: msgData, isLoading: msgLoading } = useQuery(
    ['whatsappMessages', leadId],
    () => whatsappService.getMessages(leadId),
    { refetchInterval: 15000 }
  );
  // Templates belong to the number this lead is messaged from — its dealer's
  const { data: tplData } = useQuery(['whatsappTemplates', leadId], () => whatsappService.getTemplates(leadId), { staleTime: 5 * 60 * 1000 });

  const messages  = msgData?.data?.data?.messages || [];
  const templates = tplData?.data?.data?.templates || [];
  const fromNumber = tplData?.data?.data?.number;
  const selectedTemplate = templates.find(t => t.name === templateName);
  const paramCount = countBodyParams(selectedTemplate);

  const sendMutation = useMutation(whatsappService.sendMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsappMessages', leadId]);
      setParams([]);
    },
    onError: () => queryClient.invalidateQueries(['whatsappMessages', leadId]),
  });

  const handleSend = () => {
    if (!templateName) return;
    sendMutation.mutate({ leadId, templateName, languageCode: selectedTemplate?.language || 'en', params });
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-600" />
          WhatsApp
        </h3>
      </div>
      <div className="card-body">
        {/* Thread */}
        {msgLoading ? (
          <div className="py-6 text-center"><Spinner size="sm" /></div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No WhatsApp messages yet.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto mb-4 pr-1">
            {messages.map(m => (
              <div key={m._id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === 'outbound' ? 'bg-green-50 text-green-900' : 'bg-gray-100 text-gray-800'}`}>
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  <div className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-400">
                    <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[m.status] || 'bg-gray-300'}`} />
                    {m.status}
                    <span>&middot;</span>
                    {formatIST(m.createdAt, 'dd MMM, HH:mm')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Send form */}
        {templates.length === 0 ? (
          <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">
            {fromNumber
              ? `No approved WhatsApp templates on ${fromNumber.displayPhoneNumber}. Get templates approved in WhatsApp Manager.`
              : "No WhatsApp number is connected for this lead's dealer yet. Connect one in Settings → WhatsApp Integration."}
          </p>
        ) : (
          <div className="border-t border-gray-100 pt-3 space-y-2">
            {fromNumber && (
              <p className="text-xs text-gray-400">
                Sends from {fromNumber.displayPhoneNumber}{fromNumber.label ? ` (${fromNumber.label})` : ''}
              </p>
            )}
            <select
              value={templateName}
              onChange={e => { setTemplateName(e.target.value); setParams([]); }}
              className="input text-sm w-full"
            >
              <option value="">— Select a template —</option>
              {templates.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
            {paramCount > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: paramCount }).map((_, i) => (
                  <input
                    key={i}
                    placeholder={`{{${i + 1}}}`}
                    value={params[i] || ''}
                    onChange={e => setParams(p => { const next = [...p]; next[i] = e.target.value; return next; })}
                    className="input text-sm"
                  />
                ))}
              </div>
            )}
            <button
              onClick={handleSend}
              disabled={!templateName || sendMutation.isLoading}
              className="btn-primary text-sm w-full flex items-center justify-center gap-2"
            >
              {sendMutation.isLoading ? <Spinner size="sm" /> : <><PaperAirplaneIcon className="h-4 w-4" /> Send</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const LeadDetail = () => {
  const { id } = useParams();
  const { hasPermission, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showAssignModal, setShowAssignModal] = useState(false);

  const { data: leadData, isLoading } = useQuery(
    ['lead', id],
    () => leadService.getLead(id),
    {
      enabled: !!id,
    }
  );
  // Admins and the super admin: did this lead reach the dealer's CRM?
  const crmStatus = useCrmSyncStatus([id], currentUser);

  const { data: usersData } = useQuery(
    'users',
    userService.getUsers,
    {
      enabled: showAssignModal && hasPermission('leads', 'update'),
    }
  );

  const assignLeadMutation = useMutation(
    ({ leadId, assignData }) => leadService.assignLead(leadId, assignData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lead', id]);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
        setShowAssignModal(false);
      },
    }
  );

  const handleAssign = async (assignedTo) => {
    await assignLeadMutation.mutateAsync({
      leadId: id,
      assignData: { assignedTo, teamRole: 'sales_executive' },
    });
  };

  const lead = leadData?.data?.data?.lead || leadData?.data?.lead;
  const users = usersData?.data?.users || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Lead not found</h2>
        <p className="mt-2 text-gray-600">The lead you're looking for doesn't exist.</p>
        <div className="mt-6">
          <Link to="/leads" className="btn-primary">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Leads
          </Link>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'badge-secondary', text: 'New', icon: BuildingOfficeIcon },
      follow_up: { color: 'badge-primary', text: 'Follow Up', icon: ClockIcon },
      converted: { color: 'badge-success', text: 'Converted', icon: CheckCircleIcon },
      lost: { color: 'badge-danger', text: 'Lost', icon: XCircleIcon },
    };
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color} flex items-center`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { color: 'badge-secondary', text: 'Low' },
      medium: { color: 'badge-warning', text: 'Medium' },
      high: { color: 'badge-primary', text: 'High' },
      urgent: { color: 'badge-danger', text: 'Urgent' },
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`badge ${config.color}`}>{config.text}</span>;
  };

  const canEdit = hasPermission('leads', 'update') && 
    (currentUser.role !== 'sales_executive' || lead.assignment.assignedTo?._id === currentUser._id);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center space-x-4 min-w-0">
            <Link
              to="/leads"
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{lead.customerInfo.name}</h1>
              <p className="mt-1 text-gray-600">Lead Details</p>
              <CrmSyncBadge entries={crmStatus[id]} detailed />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {getStatusBadge(lead.status)}
            {getPriorityBadge(lead.priority)}
            {canEdit && (
              <Link
                to={`/leads/${id}/edit`}
                className="btn-outline-primary"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Lead
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Customer Information</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Contact Details</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{lead.customerInfo.phone}</span>
                    </div>
                    {lead.customerInfo.alternatePhone && (
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{lead.customerInfo.alternatePhone}</span>
                      </div>
                    )}
                    {lead.customerInfo.email && (
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{lead.customerInfo.email}</span>
                      </div>
                    )}
                    {lead.customerInfo.city && (
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {lead.customerInfo.city}
                          {lead.customerInfo.subcity && ` · ${lead.customerInfo.subcity}`}
                          {lead.customerInfo.pincode && ` · ${lead.customerInfo.pincode}`}
                        </span>
                      </div>
                    )}
                    {lead.customerInfo.address && (
                      <div className="flex items-start">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                        <span className="text-sm text-gray-900">{lead.customerInfo.address}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Lead Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        Enquiry: {formatIST(lead.leadInfo.enquiryDate, 'MMM dd, yyyy')}
                      </span>
                    </div>
                    {lead.leadInfo.brand && lead.leadInfo.model && (
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {cleanDisplay(lead.leadInfo.brand)} {cleanDisplay(lead.leadInfo.model)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500">Source:</span>
                      <span className="ml-2 text-sm text-gray-900 capitalize">
                        {lead.leadInfo.source?.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500">Buying Time:</span>
                      <span className="ml-2 text-sm text-gray-900">
                        {buyingTimeLabel(lead.leadInfo)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {lead.notes && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Notes</h4>
                  <p className="text-sm text-gray-900">{lead.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Assignment Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Assignment</h3>
              {hasPermission('leads', 'update') && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="btn-outline-primary text-sm"
                >
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  Assign
                </button>
              )}
            </div>
            <div className="card-body">
              {lead.assignment.assignedTo ? (
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
                    <span className="text-white font-medium">
                      {lead.assignment.assignedTo.firstName[0]}
                      {lead.assignment.assignedTo.lastName[0]}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {lead.assignment.assignedTo.firstName} {lead.assignment.assignedTo.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{lead.assignment.assignedTo.email}</p>
                    <p className="text-sm text-gray-500 capitalize">{lead.assignment.teamRole?.replace('_', ' ')}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No assignment</h3>
                  <p className="mt-1 text-sm text-gray-500">This lead hasn't been assigned yet.</p>
                  {hasPermission('leads', 'update') && (
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="mt-4 btn-primary text-sm"
                    >
                      Assign Lead
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Follow-ups */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Follow-ups</h3>
              <Link
                to={`/followups?leadId=${id}`}
                className="btn-outline-primary text-sm"
              >
                View All
              </Link>
            </div>
            <div className="card-body">
              <div className="text-center py-4">
                <PhoneArrowUpRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Follow-up history</h3>
                <p className="mt-1 text-sm text-gray-500">
                  View all follow-ups for this lead in the follow-ups section.
                </p>
                <Link
                  to={`/followups?leadId=${id}`}
                  className="mt-4 btn-primary text-sm"
                >
                  View Follow-ups
                </Link>
              </div>
            </div>
          </div>

          {/* WhatsApp */}
          <WhatsAppThread leadId={id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
            </div>
            <div className="card-body space-y-3">
              {hasPermission('followups', 'create') && (
                <Link
                  to={`/followups/new?leadId=${id}`}
                  className="btn-primary w-full"
                >
                  <PhoneArrowUpRightIcon className="h-4 w-4 mr-2" />
                  Schedule Follow-up
                </Link>
              )}
              {canEdit && (
                <Link
                  to={`/leads/${id}/edit`}
                  className="btn-outline-primary w-full"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Lead
                </Link>
              )}
            </div>
          </div>

          {/* Lead History */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">History</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-2 w-2 bg-primary-500 rounded-full mt-2"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">Lead created</p>
                    <p className="text-xs text-gray-500">
                      {formatIST(lead.createdAt, 'MMM dd, yyyy HH:mm')}
                    </p>
                    <p className="text-xs text-gray-500">
                      by {lead.createdBy.firstName} {lead.createdBy.lastName}
                    </p>
                  </div>
                </div>
                {lead.updatedBy && lead.updatedAt !== lead.createdAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-2 w-2 bg-secondary-500 rounded-full mt-2"></div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">Lead updated</p>
                      <p className="text-xs text-gray-500">
                        {formatIST(lead.updatedAt, 'MMM dd, yyyy HH:mm')}
                      </p>
                      <p className="text-xs text-gray-500">
                        by {lead.updatedBy.firstName} {lead.updatedBy.lastName}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAssignModal(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Assign Lead
                    </h3>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign to
                      </label>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {users.map((user) => (
                          <button
                            key={user._id}
                            onClick={() => handleAssign(user._id)}
                            disabled={assignLeadMutation.isLoading}
                            className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                                <span className="text-white font-medium text-sm">
                                  {user.firstName[0]}{user.lastName[0]}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-xs text-gray-500">{user.email}</p>
                                <p className="text-xs text-gray-500 capitalize">{user.role?.replace('_', ' ')}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowAssignModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
